import produce from 'immer';
import { Reducer } from 'redux';

import { IProcessActionsType, IProcessState, IProcessTypes } from './types';

export const INITIAL_STATE: IProcessState = {
  loading: false,
  loadingCount: false,
  loadingProcessInstance: false,
  loadingSolicitation: false,
  loadingExecution: false,
  loadingProcessCancelation: false,
  loadingProcessesSolicitations: false,
  loadingProcessesExecutions: false,
  loadingExecutionsBySolicitation: false,
  process: undefined,
  processes: undefined,
  availableProcesses: undefined,
  favoriteProcesses: undefined,
  processesSolicitations: [],
  processesCountByStatus: [],
  processesSolicitationsTotal: 0,
  processesSolicitationsInProgressTotal: 0,
  processesSolicitationsCompletedTotal: 0,
  processesSolicitationsTerminatedTotal: 0,
  processesSolicitationsInProgressPageCount: 0,
  processesSolicitationsCompletedPageCount: 0,
  processesSolicitationsTerminatedPageCount: 0,
  processesExecutions: [],
  processesExecutionsHistory: [],
  executionList: [],
  processesExecutionsTotal: 0,
  processesExecutionsInProgressTotal: 0,
  processesExecutionsCompletedTotal: 0,
  processesExecutionsTerminatedTotal: 0,
  processInstance: undefined,
  solicitationInstance: undefined,
  executionInstance: undefined,
  processesHistory: [],
  processInstantiated: false,
  processCanceled: false,
  processSuspended: false,
  hasProcessForm: false,
  total: 0,
  totalExecutions: 0,
  totalProcessesHistory: 0,
  pageCount: 0,
};

const Processes: Reducer<IProcessState, IProcessActionsType> = (
  state = INITIAL_STATE,
  action: IProcessActionsType,
) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case IProcessTypes.GET_PROCESS_REQUEST: {
        draft.loading = true;
        draft.process = undefined;
        break;
      }
      case IProcessTypes.GET_PROCESS_SUCCESS: {
        draft.loading = false;
        draft.process = action.payload.process;
        break;
      }
      case IProcessTypes.GET_PROCESS_FAILURE: {
        draft.loading = false;
        break;
      }

      case IProcessTypes.GET_PROCESSES_REQUEST: {
        draft.loading = true;
        draft.processes = undefined;
        break;
      }
      case IProcessTypes.GET_PROCESSES_SUCCESS: {
        draft.loading = false;
        draft.processes = action.payload.processes;
        break;
      }
      case IProcessTypes.GET_PROCESSES_FAILURE: {
        draft.loading = false;
        break;
      }

      case IProcessTypes.GET_AVAILABLE_AND_FAVORITE_PROCESSES_REQUEST: {
        draft.loading = true;
        draft.availableProcesses = undefined;
        break;
      }
      case IProcessTypes.GET_AVAILABLE_AND_FAVORITE_PROCESSES_SUCCESS: {
        draft.loading = false;
        draft.availableProcesses = action.payload.availableProcesses;
        draft.favoriteProcesses = action.payload.favoriteProcesses;
        break;
      }
      case IProcessTypes.GET_AVAILABLE_AND_FAVORITE_PROCESSES_FAILURE: {
        draft.loading = false;
        break;
      }

      case IProcessTypes.CREATE_FAVORITE_PROCESS_REQUEST: {
        let favoriteProcess;

        draft.availableProcesses = draft.availableProcesses?.filter(
          (availableProcess) => {
            const isAvailable =
              availableProcess.key !== action.payload.processDefinitionKey;

            if (!isAvailable) {
              favoriteProcess = availableProcess;
            }
            return isAvailable;
          },
        );

        if (favoriteProcess) {
          draft.favoriteProcesses?.push(favoriteProcess);
        }

        break;
      }
      case IProcessTypes.CREATE_FAVORITE_PROCESS_SUCCESS: {
        break;
      }
      case IProcessTypes.CREATE_FAVORITE_PROCESS_FAILURE: {
        draft.loading = false;

        let availableProcess;

        draft.favoriteProcesses = draft.favoriteProcesses?.filter(
          (favoriteProcess) => {
            const isFavorite =
              favoriteProcess.key !== action.payload.processDefinitionKey;

            if (!isFavorite) {
              availableProcess = favoriteProcess;
            }
            return isFavorite;
          },
        );

        if (availableProcess) {
          draft.availableProcesses?.push(availableProcess);
        }

        break;
      }

      case IProcessTypes.DELETE_FAVORITE_PROCESS_REQUEST: {
        let availableProcess;

        draft.favoriteProcesses = draft.favoriteProcesses?.filter(
          (favoriteProcess) => {
            const isFavorite =
              favoriteProcess.key !== action.payload.processDefinitionKey;

            if (!isFavorite) {
              availableProcess = favoriteProcess;
            }
            return isFavorite;
          },
        );

        if (availableProcess) {
          draft.availableProcesses?.push(availableProcess);
        }

        break;
      }
      case IProcessTypes.DELETE_FAVORITE_PROCESS_SUCCESS: {
        break;
      }
      case IProcessTypes.DELETE_FAVORITE_PROCESS_FAILURE: {
        let favoriteProcess;

        draft.availableProcesses = draft.availableProcesses?.filter(
          (availableProcess) => {
            const isAvailable =
              availableProcess.key === action.payload.processDefinitionKey;

            if (!isAvailable) {
              favoriteProcess = availableProcess;
            }
            return isAvailable;
          },
        );

        if (favoriteProcess) {
          draft.favoriteProcesses?.push(favoriteProcess);
        }

        break;
      }

      case IProcessTypes.GET_PROCESSES_COUNT_BY_STATUS_REQUEST: {
        draft.loading = true;
        break;
      }
      case IProcessTypes.GET_PROCESSES_COUNT_BY_STATUS_SUCCESS: {
        draft.loading = false;
        draft.processesCountByStatus = action.payload.processesCountByStatus;
        draft.total = action.payload.total;
        break;
      }
      case IProcessTypes.GET_PROCESSES_COUNT_BY_STATUS_FAILURE: {
        draft.loading = false;
        break;
      }

      case IProcessTypes.GET_PROCESSES_SOLICITATIONS_REQUEST: {
        draft.loadingProcessesSolicitations = true;
        break;
      }
      case IProcessTypes.GET_PROCESSES_SOLICITATIONS_SUCCESS: {
        draft.loadingProcessesSolicitations = false;
        draft.processesSolicitations = action.payload.processesSolicitations;

        if (action.payload.processesType === 'inProgress')
          draft.processesSolicitationsInProgressTotal = Number(
            action.payload.total,
          );
        else if (action.payload.processesType === 'completed')
          draft.processesSolicitationsCompletedTotal = Number(
            action.payload.total,
          );
        else if (action.payload.processesType === 'deleted')
          draft.processesSolicitationsTerminatedTotal = Number(
            action.payload.total,
          );
        else {
          draft.processesSolicitationsTotal = Number(action.payload.total);
        }
        break;
      }
      case IProcessTypes.GET_PROCESSES_SOLICITATIONS_FAILURE: {
        draft.loadingProcessesSolicitations = false;
        break;
      }

      case IProcessTypes.GET_PROCESSES_SOLICITATIONS_COUNT_REQUEST: {
        draft.loadingCount = true;
        break;
      }
      case IProcessTypes.GET_PROCESSES_SOLICITATIONS_COUNT_SUCCESS: {
        draft.loadingCount = true;

        const totalInProgress = Number(action.payload.totalInProgress);
        const totalCompleted = Number(action.payload.totalCompleted);
        const totalTerminated = Number(action.payload.totalTerminated);
        const totalProcessesSolicitations =
          totalInProgress + totalCompleted + totalTerminated;

        draft.processesSolicitationsTotal = totalProcessesSolicitations;
        draft.processesSolicitationsInProgressTotal = totalInProgress;
        draft.processesSolicitationsCompletedTotal = totalCompleted;
        draft.processesSolicitationsTerminatedTotal = totalTerminated;
        draft.processesSolicitationsInProgressPageCount = Number(
          action.payload.pagecountInProgress,
        );
        draft.processesSolicitationsCompletedPageCount = Number(
          action.payload.pagecountCompleted,
        );
        draft.processesSolicitationsTerminatedPageCount = Number(
          action.payload.pagecountTerminated,
        );

        break;
      }
      case IProcessTypes.GET_PROCESSES_SOLICITATIONS_COUNT_FAILURE: {
        draft.loadingCount = true;
        break;
      }

      case IProcessTypes.GET_PROCESSES_EXECUTIONS_REQUEST: {
        draft.loadingProcessesExecutions = true;
        break;
      }
      case IProcessTypes.GET_PROCESSES_EXECUTIONS_SUCCESS: {
        draft.loadingProcessesExecutions = false;
        draft.processesExecutions = action.payload.processesExecutions;

        if (action.payload.processesType === 'inProgress') {
          draft.processesExecutionsInProgressTotal = Number(
            action.payload.total,
          );
        } else if (action.payload.processesType === 'completed') {
          draft.processesExecutionsCompletedTotal = Number(
            action.payload.total,
          );
        } else if (action.payload.processesType === 'deleted') {
          draft.processesExecutionsTerminatedTotal = Number(
            action.payload.total,
          );
        } else {
          draft.processesExecutionsTotal = Number(action.payload.total);
        }
        break;
      }
      case IProcessTypes.GET_PROCESSES_EXECUTIONS_FAILURE: {
        draft.loadingProcessesExecutions = false;
        break;
      }

      case IProcessTypes.GET_PROCESSES_EXECUTIONS_COUNT_REQUEST: {
        draft.loadingCount = true;
        break;
      }
      case IProcessTypes.GET_PROCESSES_EXECUTIONS_COUNT_SUCCESS: {
        draft.loadingCount = false;

        const processesExecutionsInProgressTotal = Number(
          action.payload.processesExecutionsInProgressTotal,
        );
        const processesExecutionsCompletedTotal = Number(
          action.payload.processesExecutionsCompletedTotal,
        );
        const processesExecutionsTerminatedTotal = Number(
          action.payload.processesExecutionsTerminatedTotal,
        );
        const processesExecutionsTotal = Number(
          action.payload.processesExecutionsTotal,
        );

        draft.processesExecutionsTotal = processesExecutionsTotal;
        draft.processesExecutionsInProgressTotal = processesExecutionsInProgressTotal;
        draft.processesExecutionsCompletedTotal = processesExecutionsCompletedTotal;
        draft.processesExecutionsTerminatedTotal = processesExecutionsTerminatedTotal;
        break;
      }
      case IProcessTypes.GET_PROCESSES_EXECUTIONS_COUNT_FAILURE: {
        draft.loadingCount = false;
        break;
      }

      case IProcessTypes.GET_PROCESS_INSTANCE_REQUEST: {
        draft.loadingProcessInstance = true;
        draft.processInstance = undefined;
        if (action.payload.objectType === 'solicitation') {
          draft.solicitationInstance = undefined;
          draft.loadingSolicitation = true;
        } else {
          draft.executionInstance = undefined;
          draft.loadingExecution = true;
        }
        break;
      }
      case IProcessTypes.GET_PROCESS_INSTANCE_SUCCESS: {
        draft.loadingProcessInstance = false;
        draft.processInstance = action.payload.processInstance;
        if (action.payload.objectType === 'solicitation') {
          draft.solicitationInstance = action.payload.processInstance;
          draft.loadingSolicitation = false;
        } else {
          draft.executionInstance = action.payload.processInstance;
          draft.loadingExecution = false;
        }
        break;
      }
      case IProcessTypes.GET_PROCESS_INSTANCE_FAILURE: {
        draft.loadingProcessInstance = false;
        draft.processInstance = undefined;
        if (action.payload.objectType === 'solicitation') {
          draft.solicitationInstance = undefined;
          draft.loadingSolicitation = false;
        } else {
          draft.executionInstance = undefined;
          draft.loadingExecution = false;
        }
        break;
      }

      case IProcessTypes.INSTANTIATE_PROCESS_REQUEST: {
        draft.loadingProcessInstance = true;
        draft.processInstantiated = false;
        break;
      }
      case IProcessTypes.INSTANTIATE_PROCESS_SUCCESS: {
        draft.loadingProcessInstance = false;
        draft.processInstantiated = true;

        const total = draft.processesSolicitationsTotal + 1;
        const totalInProgress = draft.processesSolicitationsInProgressTotal + 1;

        draft.processesSolicitationsTotal = total;
        draft.processesSolicitationsInProgressTotal = totalInProgress;

        break;
      }
      case IProcessTypes.INSTANTIATE_PROCESS_FAILURE: {
        draft.loadingProcessInstance = false;
        draft.processInstantiated = false;
        break;
      }

      case IProcessTypes.GET_PROCESS_XML_REQUEST: {
        draft.loading = true;
        draft.process = undefined;
        break;
      }
      case IProcessTypes.GET_PROCESS_XML_SUCCESS: {
        draft.loading = false;
        draft.process = action.payload.process;
        break;
      }
      case IProcessTypes.GET_PROCESS_XML_FAILURE: {
        draft.loading = false;
        break;
      }

      case IProcessTypes.GET_PROCESS_FORM_REQUEST: {
        draft.loading = true;
        draft.processSuspended = false;
        draft.hasProcessForm = false;

        break;
      }
      case IProcessTypes.GET_PROCESS_FORM_SUCCESS: {
        draft.loading = false;
        draft.hasProcessForm = true;

        if (draft.process) {
          draft.process.form = action.payload.form;
        }
        break;
      }
      case IProcessTypes.GET_PROCESS_FORM_FAILURE: {
        draft.loading = false;
        draft.processSuspended = true;
        draft.hasProcessForm = false;

        if (draft.process) {
          draft.process.suspended = true;
        }
        break;
      }

      case IProcessTypes.CANCEL_PROCESS_SOLICITATIONS_REQUEST: {
        draft.loadingProcessCancelation = true;
        draft.processCanceled = false;
        break;
      }
      case IProcessTypes.CANCEL_PROCESS_SOLICITATIONS_SUCCESS: {
        draft.loadingProcessCancelation = false;
        draft.processCanceled = true;

        const filteredProcessesSolicitations = draft.processesSolicitations.filter(
          (processInstance) =>
            processInstance.id !== action.payload.processInstanceId,
        );
        draft.processesSolicitations = filteredProcessesSolicitations;

        draft.processesSolicitationsTerminatedTotal += 1;
        draft.processesSolicitationsInProgressTotal -= 1;

        break;
      }
      case IProcessTypes.CANCEL_PROCESS_SOLICITATIONS_FAILURE: {
        draft.loadingProcessCancelation = false;
        draft.processCanceled = true;
        break;
      }

      case IProcessTypes.CANCEL_PROCESS_EXECUTIONS_REQUEST: {
        draft.loadingProcessCancelation = true;
        draft.processCanceled = false;
        break;
      }
      case IProcessTypes.CANCEL_PROCESS_EXECUTIONS_SUCCESS: {
        draft.loadingProcessCancelation = false;
        draft.processCanceled = true;

        const filteredProcessesExecutions = draft.processesExecutions.filter(
          (processInstance) =>
            processInstance.id !== action.payload.processInstanceId,
        );
        draft.processesExecutions = filteredProcessesExecutions;

        draft.processesExecutionsInProgressTotal -= 1;
        draft.processesExecutionsTerminatedTotal += 1;

        break;
      }
      case IProcessTypes.CANCEL_PROCESS_EXECUTIONS_FAILURE: {
        draft.loadingProcessCancelation = false;
        draft.processCanceled = false;
        break;
      }

      case IProcessTypes.GET_EXECUTION_LIST_BY_SOLICITATION_REQUEST: {
        draft.loadingExecutionsBySolicitation = true;
        draft.executionList = [];
        break;
      }
      case IProcessTypes.GET_EXECUTION_LIST_BY_SOLICITATION_SUCCESS: {
        draft.loadingExecutionsBySolicitation = false;
        draft.executionList = action.payload.executions;
        break;
      }
      case IProcessTypes.GET_EXECUTION_LIST_BY_SOLICITATION_FAILURE: {
        draft.loadingExecutionsBySolicitation = false;
        break;
      }
      default:
    }
  });
};

export default Processes;
